import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone,
  CheckCircle,
  DeleteOutline,
  AccessTime,
  TrendingUp,
  Lightbulb,
  LocalOffer,
  SwapHoriz,
  Flight,
  CreditCard,
  Warning,
  Info,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationContext';
import { Notification, NotificationType } from '../../services/notificationService';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

/**
 * Get icon for notification type
 */
const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'points_expiry':
      return <AccessTime color="warning" />;
    case 'deal_alert':
      return <LocalOffer color="success" />;
    case 'transaction_added':
      return <CreditCard color="primary" />;
    case 'transfer_opportunity':
      return <SwapHoriz color="info" />;
    case 'milestone_reached':
      return <TrendingUp color="success" />;
    case 'price_drop':
      return <Flight color="error" />;
    case 'redemption_suggestion':
      return <Lightbulb color="warning" />;
    case 'sync_success':
      return <CheckCircleOutline color="success" />;
    case 'sync_failed':
      return <Warning color="error" />;
    default:
      return <Info color="info" />;
  }
};

/**
 * Get color for notification priority
 */
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'default';
  }
};

/**
 * Notification Center Component
 * Shows a badge with unread count and opens a menu with all notifications
 */
export const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  } = useNotifications();

  console.log('🔔 NotificationCenter render - unreadCount:', unreadCount, 'total notifications:', notifications.length);
  console.log('🔔 Available functions:', {
    markAsRead: typeof markAsRead,
    markAllAsRead: typeof markAllAsRead,
    deleteNotification: typeof deleteNotification,
    clearAll: typeof clearAll,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    try {
      await markAsRead(notification.id);
    } catch (error) {
      console.error('❌ Error marking as read:', error);
    }
    
    // Navigate if action URL exists
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      handleClose();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      console.log('🔔 Marking all notifications as read...');
      await markAllAsRead();
      console.log('✅ All notifications marked as read');
    } catch (error) {
      console.error('❌ Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      console.log('🗑️ Clearing all notifications...');
      await clearAll();
      console.log('✅ All notifications cleared successfully');
      handleClose();
    } catch (error) {
      console.error('❌ Error clearing notifications:', error);
    }
  };

  const recentNotifications = notifications.slice(0, 10); // Show only 10 most recent

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleClick}
          sx={{ 
            ml: 1,
            color: (theme) => theme.palette.mode === 'light' 
              ? 'rgba(26, 54, 93, 0.7)' 
              : 'rgba(255, 255, 255, 0.7)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'light' 
                ? 'rgba(56, 178, 172, 0.08)' 
                : 'rgba(79, 209, 199, 0.08)',
              color: (theme) => theme.palette.mode === 'light' 
                ? '#1A365D' 
                : '#ffffff',
              transform: 'scale(1.05)',
            },
          }}
        >
          {unreadCount > 0 ? <NotificationsIcon fontSize="medium" /> : <NotificationsNone fontSize="medium" />}
        </IconButton>
        
        {/* Compact Badge - Top Right Corner */}
        {unreadCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              fontSize: '0.625rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: unreadCount > 9 ? '0 5px' : '0',
              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.9)',
              zIndex: 1,
              pointerEvents: 'none',
              animation: 'badgePulse 2s ease-in-out infinite',
              '@keyframes badgePulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                  boxShadow: '0 2px 8px rgba(220, 38, 38, 0.5), 0 0 0 2px rgba(255, 255, 255, 0.9)',
                },
                '50%': {
                  transform: 'scale(1.08)',
                  boxShadow: '0 3px 10px rgba(220, 38, 38, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.95)',
                },
              },
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            mt: 1.5,
            borderRadius: 2,
            boxShadow: 3,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip 
                label={`${unreadCount} new`} 
                size="small" 
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          
          {notifications.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<CheckCircle />}
                  onClick={(e) => {
                    console.log('✅ Mark all read button clicked!', e);
                    handleMarkAllRead();
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Mark all read
                </Button>
              )}
              <Button
                size="small"
                startIcon={<DeleteOutline />}
                onClick={(e) => {
                  console.log('🗑️ Clear all button clicked!', e);
                  handleClearAll();
                }}
                color="error"
                sx={{ textTransform: 'none' }}
              >
                Clear all
              </Button>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Notifications List */}
        {recentNotifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNone sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 450, overflow: 'auto' }}>
            {recentNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  disablePadding
                  secondaryAction={
                    notification.dismissible && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await deleteNotification(notification.id);
                            console.log('✅ Notification deleted:', notification.id);
                          } catch (error) {
                            console.error('❌ Error deleting notification:', error);
                          }
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )
                  }
                  sx={{
                    backgroundColor: notification.read 
                      ? 'transparent' 
                      : (theme) => theme.palette.mode === 'light' 
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'rgba(144, 202, 249, 0.08)',
                  }}
                >
                <ListItemButton 
                  onClick={() => handleNotificationClick(notification)}
                  sx={{ 
                    py: 1.5,
                    pr: notification.dismissible ? 6 : 2, // Make room for delete button
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: 'transparent',
                        border: 1,
                        borderColor: 'divider',
                        width: 40,
                        height: 40,
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: notification.read ? 500 : 700,
                            flex: 1,
                            lineHeight: 1.3,
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {notification.priority === 'urgent' && (
                          <Chip 
                            label="Urgent" 
                            size="small" 
                            color="error"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ 
                            mb: 0.5,
                            lineHeight: 1.4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            opacity: 0.7,
                          }}
                        >
                          <AccessTime sx={{ fontSize: '0.875rem' }} />
                          {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              </ListItem>
              {index < recentNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Footer */}
        {notifications.length > 10 && [
          <Divider key="divider" />,
          <Box key="footer" sx={{ p: 1.5, textAlign: 'center' }}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                navigate('/settings', { state: { tab: 3 } }); // Navigate to notifications tab
                handleClose();
              }}
              sx={{ textTransform: 'none' }}
            >
              View All Notifications
            </Button>
          </Box>
        ]}
      </Menu>
    </>
  );
};

export default NotificationCenter;
